/**
 * What a step Cleo runs on this Mac will do, in plain words, for its approval card: "Open example.com in a preview
 * tab", never "Let Cleo use preview_open" (0.0.202 known issue, Fast). Read only from the call's own tool name and
 * arguments, and kept short: the card shows the whole request under "Show request". Every engine's card, on the
 * Desk and on Pocket, takes its headline from here (approvalCallWillRun), so a step reads the same everywhere.
 *
 * A headline never understates what the call does: a tool whose effect depends on an argument is worded by that
 * argument, and an action not described here, or arguments that cannot be read, give null, so the card names the
 * tool instead of guessing.
 */

const QUOTE_MAX = 60;
const HOST_MAX = 80;

const record = (value: unknown): Record<string, unknown> =>
  value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
/** Display text: whitespace (tabs, newlines) folded, control and bidirectional-override characters removed. */
const text = (value: unknown): string | undefined => {
  if (typeof value !== "string") return undefined;
  const shown = value
    .replace(/[\u0000-\u001f\u007f‎‏‪-‮⁦-⁩]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return shown === "" ? undefined : shown;
};
const quoted = (value: string): string =>
  `“${value.length > QUOTE_MAX ? `${value.slice(0, QUOTE_MAX - 1)}…` : value}”`;

/**
 * The host a web address goes to, as the browser reads it (WHATWG URL parsing, which removes tabs and newlines the
 * same way navigation does). Never the raw text: an address that cannot be read gives undefined. A long host keeps
 * its end, where the domain that owns it is.
 */
function site(value: unknown): string | undefined {
  if (typeof value !== "string" || value.trim() === "") return undefined;
  const raw = value.trim();
  let host: string;
  try {
    host = new URL(/^[a-z][a-z0-9+.-]*:/i.test(raw) ? raw : `https://${raw}`).hostname;
  } catch {
    return undefined;
  }
  if (host === "") return undefined;
  host = host.replace(/^www\./, "");
  return host.length > HOST_MAX ? `…${host.slice(host.length - (HOST_MAX - 1))}` : host;
}

const MODIFIER_NAMES: Readonly<Record<string, string>> = {
  Meta: "Command",
  Control: "Control",
  Alt: "Option",
  Shift: "Shift",
};

function previewWords(tool: string, args: Record<string, unknown>): string | null {
  const where = "in the preview tab";
  switch (tool) {
    case "preview_open": {
      const to = site(args.url);
      return to !== undefined ? `Open ${to} in a preview tab` : "Open a page in a preview tab";
    }
    case "preview_navigate": {
      const to = site(args.url);
      return to !== undefined ? `Go to ${to} ${where}` : `Go to another page ${where}`;
    }
    case "preview_type": {
      const typed = text(args.text);
      const clear = args.clear === true;
      if (clear)
        return typed !== undefined
          ? `Clear a field and type ${quoted(typed)} ${where}`
          : `Clear a field ${where}`;
      return typed !== undefined ? `Type ${quoted(typed)} ${where}` : `Type ${where}`;
    }
    case "preview_click":
      return `Click on the page ${where}`;
    case "preview_press": {
      const key = text(args.key);
      if (key === undefined) return `Press a key ${where}`;
      const modifiers = Array.isArray(args.modifiers)
        ? args.modifiers.flatMap((modifier) =>
            typeof modifier === "string" && MODIFIER_NAMES[modifier]
              ? [MODIFIER_NAMES[modifier]]
              : [],
          )
        : [];
      const combination = [...new Set(modifiers), key.length === 1 ? key.toUpperCase() : key].join(
        "+",
      );
      return `Press ${combination} ${where}`;
    }
    case "preview_scroll":
      return `Scroll the page ${where}`;
    case "preview_evaluate":
      return `Run a script on the page ${where}`;
    case "preview_snapshot":
      return "Look at the page in the preview tab";
    case "preview_status":
      return "Check the preview tab";
    case "preview_wait_for":
      return `Wait for the page ${where}`;
    case "preview_resize":
      return "Resize the preview tab";
    case "preview_set_appearance": {
      const scheme = text(args.colorScheme);
      return scheme !== undefined
        ? `Show the preview tab in ${scheme} mode`
        : "Change how the preview tab looks";
    }
    case "preview_recording_start":
      return "Start recording the preview tab";
    case "preview_recording_stop":
      return "Stop recording the preview tab";
    default:
      return null;
  }
}

/** music_* runs whichever music service the person chose; spotify_* runs Spotify. */
function musicWords(tool: string, args: Record<string, unknown>): string | null {
  const spotify = tool.startsWith("spotify_");
  const on = spotify ? " on Spotify" : "";
  switch (tool.replace(/^(music|spotify)_/, "")) {
    case "search": {
      const query = text(args.query);
      return query !== undefined ? `Search for ${quoted(query)}${on}` : `Search for music${on}`;
    }
    case "load":
      return args.play === true ? `Play music${on}` : `Load music${on}, without playing it`;
    case "queue":
      return `Add to what plays next${on}`;
    case "transport": {
      const action = text(args.action);
      const words: Readonly<Record<string, string>> = {
        toggle: "Play or pause",
        play: "Play",
        pause: "Pause",
        next: "Skip to the next song",
        previous: "Go back to the previous song",
        seek: "Move to another point in the song",
        volume: "Change the volume",
        shuffle: "Change shuffle",
        repeat: "Change repeat",
      };
      if (action === "transfer") return `Move playback to another device${on}`;
      return action !== undefined && words[action] !== undefined
        ? `${words[action]}${spotify ? " on Spotify" : " the music"}`
        : `Control playback${on}`;
    }
    case "devices":
      return `Look at the devices music can play on${on}`;
    case "transfer":
      return `Move playback to another device${on}`;
    case "snapshot":
      return `See what is playing${on}`;
    case "connect":
      return spotify ? "Connect Spotify" : "Connect your music service";
    case "sign_in":
      return "Sign in to Spotify";
    case "create_playlist": {
      const name = text(args.name);
      return name !== undefined
        ? `Create the playlist ${quoted(name)}${on}`
        : `Create a playlist${on}`;
    }
    case "read_playlist":
      return `Read a playlist${on}`;
    default:
      return null;
  }
}

const by =
  (field: string, words: Readonly<Record<string, string>>) =>
  (args: Record<string, unknown>): string | null => {
    const value = args[field];
    return typeof value === "string" ? (words[value] ?? null) : null;
  };

function helperMessage(args: Record<string, unknown>): string | null {
  const sent = text(args.text);
  const later = args.afterResponse === true ? ", after its current reply" : "";
  switch (args.queueAction) {
    case undefined:
      if (typeof args.queueId === "string")
        return `Send a waiting message to a helper conversation${later}`;
      return sent !== undefined
        ? `Send ${quoted(sent)} to a helper conversation${later}`
        : `Send a message to a helper conversation${later}`;
    case "add":
      return sent !== undefined
        ? `Keep ${quoted(sent)} waiting for a helper conversation, without sending it`
        : "Keep a message waiting for a helper conversation, without sending it";
    case "edit":
      return "Change a message waiting for a helper conversation";
    case "hold":
      return "Hold back a message waiting for a helper conversation";
    case "delete":
      return "Delete a message waiting for a helper conversation";
    case "restore":
      return "Restore a deleted message for a helper conversation";
    case "interrupt":
      return "Stop a helper conversation's current reply and send a waiting message first";
    default:
      return null;
  }
}

function mediaJob(args: Record<string, unknown>): string {
  const operation = record(args.request).operation ?? args.operation;
  if (typeof operation === "string") {
    if (operation.startsWith("image."))
      return operation === "image.edit" ? "Edit an image" : "Make an image";
    if (operation.startsWith("video.")) return "Make a video";
    if (operation === "audio.speak") return "Make spoken audio";
    if (operation.startsWith("audio.")) return "Make audio";
  }
  return "Make an image, a video or audio";
}

const WORDS: Readonly<Record<string, (args: Record<string, unknown>) => string | null>> = {
  // Conversations and helper sessions.
  create_child_session: (args) => {
    const title = text(args.title);
    return title !== undefined
      ? `Start a helper conversation, ${quoted(title)}`
      : "Start a helper conversation";
  },
  // One-backend plan step 3, 4b-ii: a helper kept in the World, started from a conversation kept there, on World credits.
  neucleos_start_own_session: (args) => {
    const title = text(args.title);
    return title !== undefined
      ? `Start a helper session, ${quoted(title)}, on World credits`
      : "Start a helper session on World credits";
  },
  send_session_prompt: helperMessage,
  get_session_status: () => "Check on a helper conversation",
  wait_for_session: () => "Wait for a helper conversation to finish",
  list_sessions: () => "List your conversations",
  stop_session: () => "Stop a session",
  retire_session: () => "Close a helper conversation",
  // Can 80: the person's own quiet sessions, previewed then archived in one approval.
  plan_session_tidy: () => "Look for your quiet sessions to tidy",
  archive_quiet_sessions: (args) => {
    const count =
      typeof args.count === "number" && Number.isInteger(args.count) ? args.count : null;
    const days = typeof args.days === "number" && Number.isInteger(args.days) ? args.days : null;
    const quiet =
      days === null ? "quiet" : days === 1 ? "quiet for a day" : `quiet for ${days} days`;
    const where =
      args.general === true
        ? " in General"
        : args.thisRoom === true
          ? " in this room"
          : typeof args.roomId === "string"
            ? " in one room"
            : "";
    return count === null
      ? `Archive your sessions ${quiet}${where}`
      : `Archive ${count} of your sessions ${quiet}${where}`;
  },
  snapshot_conversation: () => "Save a copy of this conversation",
  rename_conversation: (args) => {
    const title = text(args.title);
    return title !== undefined
      ? `Rename this conversation to ${quoted(title)}`
      : "Rename this conversation";
  },
  regenerate_conversation_title: () => "Give this conversation a new title",
  pin_conversation: () => "Pin this conversation",
  settle_conversation: () => "Mark this conversation as settled",
  read_named_conversation: () => "Read one of your other conversations",
  create_local_draft: () => "Write a local draft on this Mac",
  cancel_conversation_summary: () => "Cancel a conversation summary",
  respond_to_approval: by("decision", {
    accept: "Approve a request waiting in another conversation",
    acceptForSession:
      "Approve a request waiting in another conversation, for the rest of that session",
    decline: "Decline a request waiting in another conversation",
    cancel: "Cancel a request waiting in another conversation",
  }),
  list_pending_approvals: () => "List what is waiting for your approval",
  // Settings, engines and sign-in.
  set_setting: (args) => {
    const key = text(args.key);
    return key !== undefined ? `Change the setting ${quoted(key)}` : "Change a setting";
  },
  get_setting: () => "Read a setting",
  list_settings: () => "List your settings",
  manage_provider_key: by("operation", {
    refresh: "Check an engine connection again",
    rename: "Rename an engine connection",
    enable: "Turn on an engine connection",
    disable: "Turn off an engine connection",
    revoke: "Revoke an engine connection's key",
    remove: "Remove an engine connection for good",
  }),
  list_provider_targets: () => "List the engines this Mac can reach",
  neucleos_sign_in: () => "Sign this Mac in to Neucleos",
  // Files, folders and search.
  request_folder_access: (args) => {
    const path = text(args.path);
    return path !== undefined ? `Use the folder ${path}` : "Use a folder on this Mac";
  },
  request_protected_access: (args) => {
    const path = text(args.path);
    return path !== undefined
      ? `Open the protected place ${path}`
      : "Open a protected place on this Mac";
  },
  search_all: (args) => {
    const query = text(args.query);
    return query !== undefined
      ? `Search for ${quoted(query)}`
      : "Search your files and conversations";
  },
  search_tools: () => "Look for one of Cleo's tools",
  // Media and music making.
  create_media_job: mediaJob,
  read_media_job: () => "Check on media being made",
  cancel_media_job: () => "Stop making media",
  list_media_models: () => "List the models that make images, video and audio",
  save_attachment_to_media: by("action", {
    read: "Look at an attachment",
    save: "Save an attachment to your Media",
  }),
  compose_music: (args) =>
    args.action === undefined
      ? "Write or change a piece of music"
      : by("action", {
          "audio.retain": "Keep a recording in a piece of music",
          "audio.separate": "Separate a recording into its parts",
          "audio.cancel": "Stop separating a recording",
          "project.import": "Bring a piece of music into this session",
          "sound.insert": "Add a sound to a piece of music",
        })(args),
  read_music_composition: () => "Read a piece of music",
  render_music_composition: () => "Export a piece of music as a file",
  // Websites and apps.
  website_application: (args) => {
    const name = text(args.siteKey);
    const of = name !== undefined ? ` ${quoted(name)}` : "";
    return by("action", {
      read: `Read the website${of}`,
      build: `Build the website${of}, without publishing it`,
      review: `Prepare the website${of} for review, without publishing it`,
      publish: `Publish the website${of} at its public address`,
      "source-list": `List the files of the website${of}`,
      "source-read": `Read the source of the website${of}`,
      "source-save": `Change the source of the website${of}`,
    })(args);
  },
  mobile_app: by("action", {
    create: "Create a mobile app",
    "source-list": "List a mobile app's files",
    "source-read": "Read a mobile app's source",
    "source-save": "Change a mobile app's source",
    start: "Start a mobile app",
    run: "Run a mobile app",
    stop: "Stop a mobile app",
    observe: "Look at a running mobile app",
    act: "Use a running mobile app",
  }),
  // The Desk itself, and what a room holds.
  capture_desk_view: (args) =>
    args.interaction !== undefined ? "Use the Desk's screen" : "Look at the Desk's screen",
  open_member_board: () => "Open a member's board",
  list_room_members: () => "List what this room holds",
  read_room_member: () => "Read something this room holds",
  walk_context_members: () => "Look through this conversation's context",
  request_context_members: () => "Add to this conversation's context",
};

/** Plain words for what a Cleo tool call will do on this Mac, or null when they cannot be told for this call. */
export function deskToolHeadline(tool: string, args?: unknown): string | null {
  const name = tool.trim();
  const input = record(args);
  if (name.startsWith("preview_")) return previewWords(name, input);
  if (name.startsWith("music_") || name.startsWith("spotify_")) return musicWords(name, input);
  return WORDS[name]?.(input) ?? null;
}
